import React, { FC, useCallback, useEffect } from 'react';
import { useFetch } from '../../../shared';
import { Loading } from '../../../shared/components';
import apiRoutes from '../../../../config/api/routes';
import { Exercise, ExerciseSeriesType } from '../../../../config/api/models/Exercise';
import SeriesDetailHeading from './SeriesDetailHeading';
import { ExerciseList } from './ExerciseList';
import { AddExerciseForm } from './ExerciseForm';
import { useI18nPath } from '../../../../config/clientRoutes/hooks';
import { useParams } from 'react-router';
import { DropResult } from 'react-beautiful-dnd';
import isEqual from 'lodash/isEqual';
import { UrlParams } from '../../../shared/types';
import { useAppSelector, useAppDispatch } from '../../../shared/hooks/useAppDispatch';
import { fetchExercises } from '../../../shared/slices/blocks/api/blocks';
import { updateExercisesOrder } from '../../../shared/slices/blocks/entity/BlocksSlice';
import { mapBackendPageToPage } from '../../../../config/api/models/Page';

type Props = {
  type: ExerciseSeriesType;
  seriesId: string;
  isTrajectoryViewEditMode?: boolean;
};

const SeriesDetailContent: FC<Props> = ({ type, seriesId, isTrajectoryViewEditMode = false }) => {
  const { getPath } = useI18nPath();
  const { bookId, chapterId, trajectoryId, sectionCode, sectionName } = useParams<UrlParams>();

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchExercises(seriesId));
  }, [seriesId, dispatch]);

  const exercises = useAppSelector((state) => state.blocks.exercises);
  const loading = useAppSelector((state) => state.blocks.loading);

  const [orderBlocksResponse, orderBlocks, resetOrderBlocks] = useFetch('', {
    method: 'PATCH',
    mapper: mapBackendPageToPage,
  });

  useEffect(() => {
    if (orderBlocksResponse.success) {
      resetOrderBlocks();
    }
  }, [orderBlocksResponse.success, resetOrderBlocks]);

  const handleOrderExercises = useCallback(
    ({ source, destination }: DropResult) => {
      if (!loading && exercises && !!destination) {
        const newExerciseList: Exercise[] = [...exercises];
        const item = newExerciseList.splice(source.index, 1)[0];
        newExerciseList.splice(destination.index, 0, item);
        const newIdOrder = newExerciseList.map((exercise: Exercise) => {
          return {
            id: exercise.id,
          };
        });

        if (!isEqual(newExerciseList, exercises)) {
          orderBlocks({
            path: apiRoutes.orderBlocks(bookId!, seriesId),
            body: newIdOrder,
          });

          dispatch(updateExercisesOrder(newExerciseList));
        }
      }
    },
    [exercises, orderBlocks, seriesId, loading, bookId, dispatch]
  );

  return (
    <>
      <SeriesDetailHeading
        seriesId={seriesId}
        returnPath={getPath(isTrajectoryViewEditMode ? 'trajectoryView' : 'trajectory', {
          bookId,
          chapterId,
          trajectoryId,
          'sectionCode?': sectionCode,
          'sectionName?': sectionName,
        })}
        mode={isTrajectoryViewEditMode ? 'edit' : 'read'}
      />
      {loading && <Loading />}
      {!loading && exercises && (
        <ExerciseList
          mode="edit"
          seriesId={seriesId}
          type={type}
          exercises={exercises}
          onUpdateExercise={() => null}
          onOrderExercises={handleOrderExercises}
        />
      )}
      <AddExerciseForm seriesId={seriesId} />
    </>
  );
};

export default SeriesDetailContent;
